/**
 * Мок заявок клиник: изменяемые строки (смена статуса пишет в ряд, как BE),
 * сборка страницы с фильтром по статусу, пагинацией и счётчиками.
 */

import type {
  ApplicationItem,
  ApplicationsPage,
  ApplicationStatus,
  GetApplicationsRequest,
} from './types';

const ago = (hours: number): string => new Date(Date.now() - hours * 3_600_000).toISOString();

type Seed = [
  hoursAgo: number,
  company: string,
  city: string,
  doctors: ApplicationItem['doctorsCount'],
  phone: string,
  status: ApplicationStatus,
];

const SEEDS: Seed[] = [
  [2, 'Дента+', 'Бишкек', '6-10', '+996700481200', 'pending'],
  [5, 'Ак-Тилек Дент', 'Ош', '2-5', '+996555207714', 'pending'],
  [22, 'Стом-Сервис', 'Бишкек', '10+', '+996770990412', 'pending'],
  [30, 'Нур Дентал', 'Каракол', '2-5', '+996500118226', 'pending'],
  [52, 'Байтик Стом', 'Бишкек', '6-10', '+996707333108', 'reviewed'],
  [96, 'Асыл-Дент', 'Джалал-Абад', '2-5', '+996772648551', 'reviewed'],
  [140, 'Прима Клиник', 'Бишкек', '6-10', '+996709220473', 'rejected'],
  [190, 'Дент Хаус', 'Токмок', '2-5', '+996550774390', 'reviewed'],
];

/**
 * Каждая третья заявка — лид с лендинга: имя и телефон, без названия,
 * города и числа врачей. Так на моках видно то же, что увидит
 * менеджер в проде, и пустые поля не всплывут впервые на бою.
 */
const LEAD_NAMES = ['Айгүл Осмонова', 'Данияр Асанов', 'Чолпон Маматова'];

export const MOCK_APPLICATIONS: ApplicationItem[] = SEEDS.map(
  ([hours, companyName, cityName, doctorsCount, phone, status], i) => {
    const lead = i % 3 === 2;
    return {
      id: `app-${i + 1}`,
      companyName: lead ? null : companyName,
      cityName: lead ? null : cityName,
      doctorsCount: lead ? null : doctorsCount,
      fullName: lead ? (LEAD_NAMES[i % LEAD_NAMES.length] ?? 'Клиент') : null,
      phone,
      phoneVerified: !lead,
      source: lead ? 'instagram' : null,
      comment: lead && i === 2 ? 'Хотим попробовать, у нас два кресла' : null,
      status,
      convertedClinicId: null,
      reviewedByName: status === 'pending' ? null : 'Команда Skrezo',
      reviewedAt: status === 'pending' ? null : ago(hours - 1),
      createdAt: ago(hours),
    };
  },
);

/**
 * Мок-конвертация: по заявке завели клиента.
 *
 * Нужно, чтобы на моках очередь вела себя как на бою: без этого
 * карточка продолжала бы предлагать «Завести клиента» после создания,
 * и дырку в сценарии мы бы нашли уже в проде.
 */
export const markApplicationConvertedMock = (applicationId: string, clinicId: string): void => {
  const row = MOCK_APPLICATIONS.find((a) => a.id === applicationId);
  if (row !== undefined) row.convertedClinicId = clinicId;
};

/** Мок-смена статуса: пишет в ряд и возвращает свежую копию (как BE). */
export const applyStatusMock = (id: string, status: ApplicationStatus): ApplicationItem | null => {
  const row = MOCK_APPLICATIONS.find((a) => a.id === id);
  if (row === undefined) return null;
  row.status = status;
  row.reviewedByName = status === 'pending' ? null : 'Команда Skrezo';
  row.reviewedAt = status === 'pending' ? null : new Date().toISOString();
  return {...row};
};

export const buildApplicationsPage = ({
  status,
  phoneVerified,
  page = 1,
  pageSize = 25,
}: GetApplicationsRequest): ApplicationsPage => {
  /* Счётчики считаем так же, как бэк: с учётом происхождения, но без
     учёта статуса. Иначе у выбранного чипа всегда стояло бы его же
     число, а у остальных — нули. */
  const byOrigin =
    phoneVerified === undefined
      ? MOCK_APPLICATIONS
      : MOCK_APPLICATIONS.filter((a) => a.phoneVerified === phoneVerified);

  const statusCounts: ApplicationsPage['statusCounts'] = {};
  for (const a of byOrigin) statusCounts[a.status] = (statusCounts[a.status] ?? 0) + 1;

  const filtered = status === undefined ? byOrigin : byOrigin.filter((a) => a.status === status);
  const start = (page - 1) * pageSize;
  return {
    list: filtered.slice(start, start + pageSize).map((a) => ({...a})),
    totalCount: filtered.length,
    currentPage: page,
    pageSize,
    statusCounts,
  };
};
